import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  AlertTriangle, CalendarClock, CalendarDays, CheckCircle2, ClipboardList, FileCheck2, FileText,
  Flag, ListChecks, RotateCcw,
} from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { formatDay } from '@/lib/tenantLeaseDetails';
import { milestonesQuery, plansQuery, projectsQuery, tasksQuery } from '@/lib/portfolioQueries';
import {
  myDay, needsMeCount, dueLabel, seesAllProjects, SCOPE_ALL, SCOPE_MINE, LOOK_AHEAD_DAYS,
} from '@/lib/myDay';

/* The page's sections, in the order a person would work them. Coming up is
   last and is not counted in "need you today": it is warning, not work. */
const SECTIONS = [
  { key: 'overdue', title: 'Overdue', icon: AlertTriangle, color: 'text-red-500', late: true },
  { key: 'today', title: 'Due today', icon: CalendarClock, color: 'text-amber-600' },
  { key: 'toReview', title: 'Waiting for review', icon: FileCheck2, color: 'text-amber-600' },
  { key: 'sentBack', title: 'Sent back to revise', icon: RotateCcw, color: 'text-orange-600' },
  { key: 'leaseBlank', title: 'Lease details still blank', icon: ClipboardList, color: 'text-primary' },
  { key: 'comingUp', title: `Coming up in the next ${LOOK_AHEAD_DAYS} days`, icon: CalendarDays, color: 'text-muted-foreground' },
];

const TYPE_ICONS = { task: ListChecks, milestone: Flag, plan: FileText, lease: ClipboardList };

function ItemRow({ item, late }) {
  const Icon = TYPE_ICONS[item.type] ?? ListChecks;
  const when = dueLabel(item.days);
  return (
    <Link to={item.href}>
      <div className={`flex items-start justify-between gap-4 bg-card border rounded-lg px-4 py-2.5 text-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer ${late ? 'border-red-100' : ''}`}>
        <div className="flex items-start gap-2.5 min-w-0">
          <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <span className="font-medium">{item.title}</span>
            <span className="text-muted-foreground text-xs ml-2">· {item.project.name}</span>
            {item.detail && <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.detail}</p>}
          </div>
        </div>
        {when && (
          <span
            className={`text-xs whitespace-nowrap font-medium ${late ? 'text-red-500' : 'text-muted-foreground'}`}
            title={item.dueDate ? formatDay(item.dueDate) : undefined}
          >
            {when}
          </span>
        )}
      </div>
    </Link>
  );
}

export default function MyDay() {
  const [scope, setScope] = useState(SCOPE_MINE);
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me(),
    staleTime: 60_000,
  });
  /* The Dashboard's own lists, under the same keys, so moving between the two
     pages reads from the cache rather than fetching everything again. */
  const projectsQ = useQuery(projectsQuery);
  const tasksQ = useQuery(tasksQuery);
  const milestonesQ = useQuery(milestonesQuery);
  const plansQ = useQuery(plansQuery);
  const queries = [projectsQ, tasksQ, milestonesQ, plansQ];

  /* Waiting for everything before saying anything: "Nothing needs you today"
     shown for the second before the tasks arrive would be a false all-clear. */
  const loading = !me || queries.some(q => q.isLoading);
  const failed = queries.some(q => q.isError);

  const offerAll = seesAllProjects(me);
  const shownScope = offerAll ? scope : SCOPE_MINE;
  const day = loading ? null : myDay({
    projects: projectsQ.data ?? [],
    tasks: tasksQ.data ?? [],
    milestones: milestonesQ.data ?? [],
    plans: plansQ.data ?? [],
    user: me,
    scope: shownScope,
  });
  const count = day ? needsMeCount(day) : 0;

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Day</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {format(new Date(), 'EEEE, MMMM d')}
            {day && ` · ${count === 0 ? 'nothing needs you' : `${count} thing${count === 1 ? '' : 's'} need${count === 1 ? 's' : ''} you`}`}
          </p>
        </div>
        {/* Only for the tiers that can see every project. A Property Manager
            already sees only their own, so the switch would change nothing. */}
        {offerAll && (
          <div className="flex items-center gap-2">
            <Switch
              id="myday-scope"
              checked={scope === SCOPE_ALL}
              onCheckedChange={on => setScope(on ? SCOPE_ALL : SCOPE_MINE)}
            />
            <Label htmlFor="myday-scope" className="text-sm">All projects</Label>
          </div>
        )}
      </div>

      {failed ? (
        <p className="text-sm text-red-600">
          Some of your projects could not be loaded, so this list may be incomplete. Refresh the page to try again.
        </p>
      ) : loading ? (
        <div role="status" aria-label="Loading My Day" className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {count === 0 && (
            <div className="flex items-center gap-3 bg-card border rounded-lg px-4 py-4 text-sm">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span>
                Nothing needs you today.
                {shownScope === SCOPE_MINE && offerAll && ' This is only the projects that name you - switch on All projects to see the rest.'}
              </span>
            </div>
          )}
          {SECTIONS.map(section => {
            const items = day[section.key];
            if (!items.length) return null;
            return (
              <section key={section.key} aria-labelledby={`myday-${section.key}`}>
                <h2 id={`myday-${section.key}`} className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                  <section.icon className={`w-4 h-4 ${section.color}`} /> {section.title}
                  <span className="text-muted-foreground font-normal">({items.length})</span>
                </h2>
                <div className="space-y-2">
                  {items.map(item => <ItemRow key={item.key} item={item} late={section.late} />)}
                </div>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
